/**
 * navigation/routes.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Every view in the product, and which console owns it.
 *
 * The keys are the design's own (`overview`, `register`, `recon`, `approvals`,
 * `router`, `settle`, `settings`, `queue`, `decided`, `rules`, `mycash`,
 * `myledger`) so a value here can be compared to the reference without a
 * translation step. Icons are the paths the design drew — see
 * components/Icon/paths.ts.
 *
 * ONE APPLICATION, NOT THREE. Switching console swaps the sidebar and the
 * content area, but there is a single shell, a single store and a single set of
 * primitives. The console comes from the signed-in user's permissions
 * (`bootstrap.console`) and is never chosen in the UI — the prototype's role
 * selector was scaffolding and is not shipped.
 * ──────────────────────────────────────────────────────────────────────────
 */
import type { IconName } from '../components/Icon';
import type { ConsoleRole } from '../schemas/config';
import type { GateKey } from '../lib/gate';

/** Finance views. */
export type FinanceView =
  | 'overview'
  | 'register'
  | 'recon'
  | 'approvals'
  | 'router'
  | 'settle'
  | 'settings';
/** Approver views. */
export type ApproverView = 'queue' | 'decided' | 'rules';
/** Custodian views. */
export type CustodianView = 'mycash' | 'myledger';

export type ViewKey = FinanceView | ApproverView | CustodianView;

export interface NavItem {
  key: ViewKey;
  label: string;
  icon: IconName;
  /**
   * Which count the badge shows, or `null` for no badge. The value itself comes
   * from live data at render time — see Sidebar — so this only names it.
   */
  badge: 'active' | 'openRecon' | 'pending' | 'undelivered' | 'separations' | 'setup' | null;
  /** The gate that blocks this view, if any. Drives the gate bar. */
  gate?: GateKey;
}

/** Nav per console, in the design's order. The first item is the default view. */
export const NAV: Record<ConsoleRole, readonly NavItem[]> = {
  finance: [
    { key: 'overview', label: 'Overview', icon: 'overview', badge: null },
    { key: 'register', label: 'Assignment register', icon: 'register', badge: 'active' },
    { key: 'recon', label: 'Reconciliation', icon: 'recon', badge: 'openRecon', gate: 'variance_set' },
    { key: 'approvals', label: 'Approvals', icon: 'approvals', badge: 'pending' },
    { key: 'router', label: 'Sync log', icon: 'router', badge: 'undelivered', gate: 'connectors' },
    { key: 'settle', label: 'Settlement alerts', icon: 'settle', badge: 'separations', gate: 'offboard_feed' },
    { key: 'settings', label: 'Settings', icon: 'settings', badge: 'setup' },
  ],
  approver: [
    { key: 'queue', label: 'Waiting on you', icon: 'queue', badge: 'pending' },
    { key: 'decided', label: 'Decided', icon: 'decided', badge: null },
    { key: 'rules', label: 'Policy in force', icon: 'rules', badge: null },
  ],
  custodian: [
    { key: 'mycash', label: 'My cash', icon: 'mycash', badge: null },
    { key: 'myledger', label: 'My transactions', icon: 'myledger', badge: null },
  ],
};

/** The eyebrow above each console's nav. */
export const CONSOLE_TITLE: Record<ConsoleRole, string> = {
  finance: 'Finance console',
  approver: 'Approver',
  custodian: 'Custodian',
};

/** The landing view for a console. */
export const defaultView = (role: ConsoleRole): ViewKey => NAV[role][0]!.key;

/**
 * Resolve a possibly-stale view against the current console. A user whose
 * permissions changed while a tab was open must not be left on a view their
 * console does not have — they land on its default instead of on a blank page.
 */
export function resolveView(role: ConsoleRole, view: ViewKey | null | undefined): ViewKey {
  if (view && NAV[role].some((item) => item.key === view)) return view;
  return defaultView(role);
}

/** Which max-width family a view belongs to (the design centres them differently). */
export const VIEW_WIDTH: Record<ViewKey, 'default' | 'register' | 'settings' | 'approver'> = {
  overview: 'default',
  register: 'register',
  recon: 'default',
  approvals: 'default',
  router: 'default',
  settle: 'settings',
  settings: 'settings',
  queue: 'approver',
  decided: 'approver',
  rules: 'approver',
  mycash: 'default',
  myledger: 'default',
};
